import { CartItemType, ServiceType } from "@/constants/enum";
import { RestaurantCartValue } from "@/models/cart/types";
import { RestaurantCart } from "@/models/cart/restaurantCart";

import { DateTime } from "luxon";
import { isWithinBusinessHours } from "./scheduleOrderUtils";

/**
 * Converts a flat array of cart modifiers into a nested object format used by the component state.
 * * @param cartModifiers - An array of modifiers from the stored cart item.
 * @param allGroups - The list of all modifier groups available to find missing group IDs.
 * @returns A nested object mapping groupId to modifierId and its quantity.
 */
export const mapCartModifiersToState = (
  cartModifiers: any[],
  allGroups: any[]
) => {
  return cartModifiers.reduce((acc, modifier) => {
    let groupId = modifier.groupId;
    if (!groupId) {
      const foundGroup = allGroups.find((g) =>
        g.modifiers?.some((m: any) => m.id === modifier.id)
      );
      if (foundGroup) groupId = foundGroup.id;
    }

    if (groupId) {
      if (!acc[groupId]) acc[groupId] = {};

      if (modifier.quantity === undefined) {
        // handles flat list of modifiers sent by the server
        acc[groupId][modifier.id] = (acc[groupId][modifier.id] || 0) + 1;
      } else {
        // handles modifiers with a quantity property adding to cart on the UI
        acc[groupId][modifier.id] = modifier.quantity;
      }
    }

    return acc;
  }, {});
};

/**
 * Filters and formats modifier groups based on item requirements and availability.
 * @param menu - The menu object containing all modifier groups.
 * @param item - The specific item being configured.
 * @param modifiers - The flat list of all available modifiers.
 * @returns An array of ModifierGroup objects ready for selection.
 */
export const getSelectableModifierGroups = (
  menu: any,
  item: any,
  modifiers: any[]
) => {
  const filteredModifierGroups: any[] = menu.modifierGroups
    ? item.modifierGroupIds.reduce((groupList: any[], groupId: string) => {
        const group = menu.modifierGroups[groupId];
        if (group) {
          groupList.push(group);
        }
        return groupList;
      }, [])
    : [];

  const selectableModifierGroups: any[] =
    filteredModifierGroups.length > 0
      ? filteredModifierGroups
          .map((group: any) => ({
            ...group,
            modifiers: modifiers.filter((mod: any) => mod.groupId === group.id),
            isSingleSelection:
              group.minRequired === 1 && group.maxAllowed === 1,
          }))
          .filter(
            (group: any) =>
              group.modifiers?.length > 0 &&
              group.modifiers?.some((modifier: any) => modifier.available)
          )
      : [];

  return { filteredModifierGroups, selectableModifierGroups };
};

/**
 * Calculates the new state for selected modifiers when a modifier is toggled.
 * @param groupId - The ID of the group the modifier belongs to.
 * @param modifierId - The ID of the modifier being toggled.
 * @param isSingleSelection - Boolean indicating if the group only allows one selection.
 * @param modifiers - The list of all available modifiers.
 * @param selectableModifierGroups - The list of filtered selectable groups.
 * @param prevSelectedModifiers - The current state of selected modifiers.
 * @returns The new selected modifiers state object.
 */
export const calculateModifierSelection = (
  groupId: string,
  modifierId: string,
  isSingleSelection: boolean,
  modifiers: any[],
  selectableModifierGroups: any[],
  prevSelectedModifiers: any
) => {
  const modifier = modifiers.find((mod: any) => mod.id === modifierId);
  if (!modifier) {
    return prevSelectedModifiers;
  }

  const group = selectableModifierGroups.find((g: any) => g.id === groupId);

  if (!group) return prevSelectedModifiers;

  const newSelectedModifiers = { ...prevSelectedModifiers };
  const groupSelections: { [key: string]: number } = {
    ...(newSelectedModifiers[groupId] || {}),
  };

  if (isSingleSelection) {
    newSelectedModifiers[groupId] = { [modifierId]: 1 };
    return newSelectedModifiers;
  }

  const isSelected = groupSelections[modifierId];

  if (isSelected) {
    delete groupSelections[modifierId];
  } else {
    const totalGroupQuantity = getGroupTotalQuantity(groupSelections);
    if (totalGroupQuantity < group.maxAllowed) {
      groupSelections[modifierId] = 1;
    }
  }
  newSelectedModifiers[groupId] = groupSelections;
  return newSelectedModifiers;
};

/**
 * Calculates the new state for selected modifiers when a specific modifier's quantity is adjusted.
 * @param groupId - The ID of the group the modifier belongs to.
 * @param modifierId - The ID of the modifier being adjusted.
 * @param newQuantity - The target quantity requested.
 * @param selectableModifierGroups - The list of filtered selectable groups.
 * @param selectedModifiers - The current state of selected modifiers.
 * @returns The new selected modifiers state object.
 */
export const calculateModifierQuantityChange = (
  groupId: string,
  modifierId: string,
  newQuantity: number,
  selectableModifierGroups: any[],
  selectedModifiers: any
) => {
  const group: any = selectableModifierGroups.find(
    (g: any) => g.id === groupId
  );
  if (!group) return selectedModifiers;

  if (newQuantity === 0) {
    const newSelectedModifiers = { ...selectedModifiers };
    if (newSelectedModifiers[groupId]?.[modifierId]) {
      delete newSelectedModifiers[groupId][modifierId];
    }
    return newSelectedModifiers;
  }

  const groupSelections = selectedModifiers[groupId] || {};
  const otherModifiersQuantity = getGroupTotalQuantity(
    Object.keys(groupSelections)
      .filter((mId) => mId !== modifierId)
      .reduce((obj: any, key) => {
        obj[key] = groupSelections[key];
        return obj;
      }, {})
  );

  const availableQuantity: number = Math.min(
    group.maxAllowed - otherModifiersQuantity,
    99
  );

  if (newQuantity >= 1 && newQuantity <= availableQuantity) {
    const newSelectedModifiers = JSON.parse(JSON.stringify(selectedModifiers));
    if (newSelectedModifiers[groupId]?.[modifierId]) {
      newSelectedModifiers[groupId][modifierId] = newQuantity;
    }
    return newSelectedModifiers;
  }

  return selectedModifiers;
};

/**
 * Validates if the current modifier selections meet the required min/max constraints.
 * @param selectableModifierGroups - The list of groups to validate against.
 * @param selectedModifiers - The current state of selected modifiers.
 * @returns A boolean indicating if all group requirements are satisfied.
 */
export const isModifierSelectionValid = (
  selectableModifierGroups: any[],
  selectedModifiers: any
) => {
  return Object.values(selectableModifierGroups || {}).every((group: any) => {
    const groupSelections: { [key: string]: number } =
      selectedModifiers[group.id] || {};
    const totalGroupQuantity = getGroupTotalQuantity(groupSelections);
    return (
      totalGroupQuantity >= group.minRequired &&
      totalGroupQuantity <= group.maxAllowed
    );
  });
};

/**
 * Determines if the add to cart button should be disabled based on item and merchant status.
 * @param item - The item being viewed.
 * @param isAcceptingOrders - Whether the merchant is currently accepting orders.
 * @returns boolean
 */
export const isAddButtonDisabled = (item: any, isAcceptingOrders: boolean) => {
  return item?.available === false || !isAcceptingOrders;
};

/**
 * Determines if the minus button should be disabled based on quantity.
 * @param isAcceptingOrders - Whether the merchant is currently accepting orders.
 * @param quantity - Current quantity.
 * @param minQuantity - MIN_QUANTITY constant.
 * @returns boolean
 */
export const checkIsMinusDisabled = (
  isAcceptingOrders: boolean,
  quantity: number | undefined,
  minQuantity: number
) => {
  if (!isAcceptingOrders) return true;
  if (quantity === undefined) return false;
  return quantity <= minQuantity;
};

/**
 * Determines if the plus button should be disabled based on quantity.
 * @param isAcceptingOrders - Whether the merchant is currently accepting orders.
 * @param quantity - Current quantity.
 * @param maxQuantity - MAX_QUANTITY constant.
 * @returns boolean
 */
export const checkIsPlusDisabled = (
  isAcceptingOrders: boolean,
  quantity: number | undefined,
  maxQuantity: number
) => {
  if (!isAcceptingOrders) return true;
  if (quantity === undefined) return false;
  return quantity >= maxQuantity;
};

/**
 * Identifies the delivery service and checks if it is the only enabled service.
 * @param merchant - The merchant object containing services.
 * @returns An object containing the deliveryService and a boolean isOnlyDeliveryEnabled.
 */
export const getDeliveryServiceStatus = (merchant: any) => {
  const enabledServices = merchant.services.filter(
    (service: any) => service.enabled
  );

  const deliveryService = merchant.services.find(
    (s: any) => s.type === ServiceType.DELIVERY
  );

  const isOnlyDeliveryEnabled =
    enabledServices.length === 1 && deliveryService?.enabled;

  return { deliveryService, isOnlyDeliveryEnabled };
};

/**
 * Prepares the payload for adding an item to the cart, including price calculation.
 * @param params - Object containing item, modifiers, and cart state.
 * @returns The prepared cart item object.
 */
export const prepareCartItem = ({
  item,
  quantity,
  selectedModifiers,
  selectableModifierGroups,
  filteredModifierGroups,
  allModifiers,
  specialInstructions,
  editingCartId,
  sessionDayPartMenu,
  getPrice,
}: any) => {
  const totalPrice = getPrice({
    item,
    filteredModifierGroups,
    allModifiers,
    selectedModifiers,
    quantity,
  });

  const newItem = RestaurantCart.buildRestaurantCartItemPayload(
    CartItemType.RESTAURANT as const,
    item as RestaurantCartValue,
    quantity,
    selectedModifiers,
    selectableModifierGroups,
    allModifiers,
    specialInstructions,
    totalPrice,
    sessionDayPartMenu?.menuId,
    editingCartId || undefined
  );

  return newItem;
};

/**
 * Calculates the message to display if an item is currently unavailable due to scheduling.
 * @param params - Object containing visibility flags, schedule data, and helper functions.
 * @returns The formatted availability message string or an empty string.
 */
export const getAvailabilityMessage = ({
  addButtonDisabled,
  selectedDayPart,
  isMerchantOpen,
  timezone,
  t,
  HHMMtoDate,
  findNextAvailableDay,
  isEmpty,
  isNil,
  businessHours,
  canScheduleOrder,
}: any) => {
  let itemNotAvailableMessage = "";
  const nowInMerchantTimeZone = DateTime.fromJSDate(new Date(), {
    zone: timezone,
  });
  const isNowWithInBusinessHours = isWithinBusinessHours(
    nowInMerchantTimeZone,
    businessHours,
    timezone
  );

  if (addButtonDisabled && !isEmpty(selectedDayPart) && isMerchantOpen) {
    if (!isNowWithInBusinessHours || !canScheduleOrder) {
      itemNotAvailableMessage = t("cannotOrder");
    } else if (
      !selectedDayPart?.activeNow &&
      !isNil(selectedDayPart?.time?.startTime)
    ) {
      const dayPartStartTime = HHMMtoDate(
        selectedDayPart?.time?.startTime.toString().padStart(4, "0"),
        timezone,
        new Date()
      ).toLocaleString({
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });

      const nextAvailableDay = findNextAvailableDay(selectedDayPart, timezone);

      const messageCode = nextAvailableDay
        ? "itemAvailableOn"
        : "itemAvailableAt";
      itemNotAvailableMessage = `${t(messageCode, {
        dayPartStartTime: dayPartStartTime,
        day: nextAvailableDay ?? "",
      })}`;
    }
  }
  return itemNotAvailableMessage;
};

/**
 * Internal helper to calculate total quantity selected within a specific group.
 */
const getGroupTotalQuantity = (
  groupSelections: Record<string, number>
): number => {
  return Object.values(groupSelections || {}).reduce(
    (acc: number, quantity: number) => acc + quantity,
    0
  );
};
